import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { fetchInvoiceDetailHeader, type InvoiceCollect } from '@/integrations/faturamento/client';
import { composeInvoiceCollectsPage } from '@/services/invoiceDetails';
import { useAppSelector } from '@/store/hooks';
import {
  COLLECT_STATUS_CONTESTED,
  collectStatusLabel,
} from '@/components/financeiro/contestacao/collectStatus';

/**
 * Exportação em CSV das coletas do extrato (MC-292).
 *
 * **Por que paginar no cliente e não pedir um endpoint.** Este hook nasceu desativado, com o
 * TODO "reativar quando backend expor endpoint de export CSV para coletas de fatura". O endpoint
 * nunca entrou em nenhuma fila e não é necessário: as coletas da fatura são compostas no front
 * (faturamento + agreements + mtr-manager), página a página, e o faturamento-api limita
 * `page_size` a 100. Uma resposta única materializaria tudo de uma vez, sem timeout no cliente;
 * o loop paginado degrada visível.
 */
const EXPORT_PAGE_SIZE = 100;
const CONCURRENT_REQUESTS = 3;

/** Rótulo vindo do `contestacao-service`; sem ele a coluna não pode afirmar o estado da coleta. */
const STATUS_UNAVAILABLE = 'Indisponível';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface UseInvoiceCollectsCSVExportParams {
  /** `sfm_id` do ciclo — o mesmo `id` da rota `/financeiro/:id`. */
  invoiceId: string;
  numeroPedido: string | null;
  /** OS na Omie: presente = Fatura, ausente = Medição. Mesma regra do filtro da listagem. */
  osCode: string | null;
  sfmId: string;
  /** Competência do ciclo, `YYYY-MM`. */
  billingPeriod: string;
  /** Ciclo de valor fixo não tem valor por coleta — a coluna de valor sai do arquivo. */
  isFixedValue: boolean;
  /**
   * Contestação ativa da coleta, na mesma regra da tela (a da coleta ou a do lançamento dela).
   * Recebido de fora para o arquivo não divergir da tabela.
   */
  hasActiveContestation: (collect: Pick<InvoiceCollect, 'id' | 'transaction_id'>) => boolean;
  /** Índice de contestações fora do ar — ver `STATUS_UNAVAILABLE`. */
  contestationsUnavailable: boolean;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY`. Sem `Date` de propósito: a string do gateway não tem fuso. */
function formatDate(date: string | null | undefined): string {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}/${month}/${year}`;
}

/** `YYYY-MM` → `Ago/2026`, mesmo formato do CSV da listagem (`useInvoicesCSVExport`). */
function formatCompetencia(competencia: string | null | undefined): string {
  if (!competencia) return '';
  const [year, month] = competencia.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return '';
  return `${MONTHS[month - 1]}/${year}`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/** Números em pt-BR (vírgula decimal), como nas demais exportações do portal. */
function formatNumber(value: number | null | undefined): string {
  if (value == null) return '';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(value);
}

function escapeCsvValue(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export const useInvoiceCollectsCSVExport = ({
  invoiceId,
  numeroPedido,
  osCode,
  sfmId,
  billingPeriod,
  isFixedValue,
  hasActiveContestation,
  contestationsUnavailable,
}: UseInvoiceCollectsCSVExportParams) => {
  const establishments = useAppSelector((s) => s.establishmentAccess.establishments);
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    const toastId = toast.loading('Exportando coletas... 0%');

    try {
      const header = await fetchInvoiceDetailHeader(invoiceId);
      const fetchPage = (page: number) =>
        composeInvoiceCollectsPage({ id: invoiceId, header, page, pageSize: EXPORT_PAGE_SIZE, lookups: { establishments } });

      const firstPage = await fetchPage(1);
      const allCollects: InvoiceCollect[] = [...(firstPage.data ?? [])];
      const totalPages = Math.max(firstPage.pagination.last_page ?? 1, 1);
      let processedPages = 1;

      const reportProgress = () => {
        toast.loading(
          `Exportando coletas... ${Math.round((processedPages / totalPages) * 100)}% (${processedPages}/${totalPages} ${totalPages === 1 ? 'página' : 'páginas'})`,
          { id: toastId },
        );
      };

      reportProgress();

      const remainingPages: number[] = [];
      for (let p = 2; p <= totalPages; p++) remainingPages.push(p);

      for (let i = 0; i < remainingPages.length; i += CONCURRENT_REQUESTS) {
        const batch = remainingPages.slice(i, i + CONCURRENT_REQUESTS);
        const results = await Promise.all(batch.map(page => fetchPage(page)));
        for (const res of results) {
          allCollects.push(...(res.data ?? []));
          processedPages++;
        }
        reportProgress();
      }

      if (allCollects.length === 0) {
        toast.warning('Nenhuma coleta encontrada para exportar', { id: toastId });
        return;
      }

      /**
       * `Tipo` e `Nº` seguem a mesma derivação do extrato: fatura é o ciclo que já tem OS na
       * Omie. Em análise interna não existe nem número de pedido nem OS — resta o id do ciclo.
       */
      const tipo = osCode ? 'Fatura' : 'Medição';
      const numero = numeroPedido ?? osCode ?? sfmId;
      const competencia = formatCompetencia(billingPeriod);

      /**
       * A coluna de valor é governada por `is_fixed_value`, não pelo que a página exibe: na tela
       * ela some quando nenhuma coleta **daquela página** tem valor, e um arquivo cujo conjunto
       * de colunas depende da página aberta não é conferível.
       */
      const showValue = !isFixedValue;

      const headers = [
        'ID Coleta',
        'Tipo',
        'Competência',
        'Nº',
        'Data',
        'Estabelecimento',
        'Resíduo',
        'Acondicionamento',
        'Unidades',
        'Quantidade coletada',
        'Unidade de medida',
        ...(showValue ? ['Valor (R$)'] : []),
        'Status coleta',
      ];

      /**
       * `Quantidade coletada` + `Unidade de medida` no lugar de "Peso (kg)": nos dois ramos do
       * gateway `weight` é alias puro de `units_collected`, e a unidade real do número está em
       * `material.unity`. Rotular tudo como kg num arquivo que o gerador manda ao contador
       * transformaria "12 lâmpadas" em "12 kg".
       */
      const rows = allCollects.map(collect => {
        const statusCell = contestationsUnavailable
          ? STATUS_UNAVAILABLE
          : collectStatusLabel(
              hasActiveContestation(collect) ? COLLECT_STATUS_CONTESTED : collect.status,
            ) ?? '';

        return [
          escapeCsvValue(collect.id),
          escapeCsvValue(tipo),
          escapeCsvValue(competencia),
          escapeCsvValue(numero),
          escapeCsvValue(formatDate(collect.planned_date)),
          escapeCsvValue(collect.establishment?.name),
          escapeCsvValue(collect.material?.name),
          escapeCsvValue(collect.packaging?.name),
          escapeCsvValue(formatNumber(collect.units)),
          escapeCsvValue(formatNumber(collect.weight)),
          escapeCsvValue(collect.material?.unity),
          ...(showValue
            ? [escapeCsvValue(collect.value != null ? formatCurrency(collect.value) : '')]
            : []),
          escapeCsvValue(statusCell),
        ];
      });

      const csv = '﻿' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${osCode ? 'fatura' : 'medicao'}_${billingPeriod}_${numeroPedido ?? sfmId}_coletas.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Coletas exportadas com sucesso! (${allCollects.length} registros)`, {
        id: toastId,
      });
    } catch (error) {
      console.error('Erro ao exportar coletas:', error);
      toast.error('Não foi possível gerar o download agora. Tente novamente.', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  }, [
    invoiceId,
    numeroPedido,
    osCode,
    sfmId,
    billingPeriod,
    isFixedValue,
    hasActiveContestation,
    contestationsUnavailable,
    isExporting,
    establishments,
  ]);

  return { exportCSV, isExporting };
};
